import { useEffect, useState, useCallback } from 'react'
import { api } from '../api'
import { Card, Button, Form, Input, InputNumber, Select, DatePicker, Tag, Empty, Spin, Space, Typography, Row, Col, Drawer, Progress, Checkbox, message, Popconfirm, Badge, Radio, Divider } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, MinusCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, EuroOutlined, CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import useIsMobile from '../hooks/useIsMobile'

const { Title, Text } = Typography
const { TextArea } = Input

const FASES_DEFAULT = [
  'Preparación del terreno',
  'Limpieza de horillas',
  'Abonado del terreno',
  'Siembra',
  'Cavar márgenes',
  'Riego',
  'Fumigación herbicida',
  'Tratamientos fungicidas',
  'Mantenimiento',
  'Recolección',
]

export default function Cultivos({ usuario }) {
  const isMobile = useIsMobile()
  const soloLectura = usuario?.rol === 'tecnico' || usuario?.rol === 'usuario'
  const [cultivos, setCultivos] = useState([])
  const [parcelas, setParcelas] = useState([])
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [mostrarForm, setMostrarForm] = useState(false)
  const [editando, setEditando] = useState(null)
  const [detalle, setDetalle] = useState(null)
  const [acciones, setAcciones] = useState([])
  const [costes, setCostes] = useState({ por_tipo: [], total: 0 })
  const [mostrarAccionForm, setMostrarAccionForm] = useState(false)
  const [accionTipo, setAccionTipo] = useState('')
  const [tipoCoste, setTipoCoste] = useState('directo')
  const [productos, setProductos] = useState([])
  const [prodsSeleccionados, setProdsSeleccionados] = useState([])
  const [fasesForm, setFasesForm] = useState(FASES_DEFAULT.map(f => ({ nombre: f, completada: false, fecha_completada: null })))
  const [nuevaFase, setNuevaFase] = useState('')
  const [form] = Form.useForm()
  const [formAccion] = Form.useForm()
  const [municipiosConfig, setMunicipiosConfig] = useState([])

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    try {
      const [c, p, muns] = await Promise.all([
        api('/cultivos'),
        api('/parcelas'),
        api('/municipios').catch(() => [])
      ])
      setCultivos(c)
      setParcelas(p)
      setMunicipiosConfig(muns)
      try { const prods = await api('/productos'); setProductos(prods) } catch { setProductos([]) }
    } catch (err) {
      console.error('Error cargando cultivos:', err)
    }
    setCargando(false)
  }

  const calcTahullas = (parcelasList) => {
    if (!parcelasList || parcelasList.length === 0) return 0
    return parcelasList.reduce((sum, p) => {
      const ha = parseFloat(p.hectareas) || 0
      const mun = municipiosConfig.find(m =>
        (m.municipio || '').toUpperCase() === (p.municipio || '').toUpperCase()
      )
      const tahullaM2 = mun ? parseFloat(mun.tahulla_m2) : 1185
      return sum + (ha * 10000 / tahullaM2)
    }, 0)
  }

  const cargarDetalle = useCallback(async (cultivoId) => {
    try {
      const [acc, cos] = await Promise.all([
        api(`/cultivos/${cultivoId}/acciones`),
        api(`/cultivos/${cultivoId}/costes`),
      ])
      setAcciones(acc)
      setCostes(cos)
    } catch (err) {
      console.error('Error cargando detalle:', err)
    }
  }, [])

  const abrirDetalle = async (cultivo) => {
    setDetalle(cultivo)
    cargarDetalle(cultivo.id)
  }

  const abrirNuevo = () => {
    setEditando(null)
    setFasesForm(FASES_DEFAULT.map(f => ({ nombre: f, completada: false, fecha_completada: null })))
    form.resetFields()
    setMostrarForm(true)
  }

  const abrirEdicion = (cultivo) => {
    setEditando(cultivo)
    setFasesForm(cultivo.fases || [])
    form.setFieldsValue({
      nombre: cultivo.nombre,
      tipo_cultivo: cultivo.tipo_cultivo,
      fecha_inicio: cultivo.fecha_inicio ? dayjs(cultivo.fecha_inicio) : null,
      fecha_fin: cultivo.fecha_fin ? dayjs(cultivo.fecha_fin) : null,
      notas: cultivo.notas,
      parcelas: cultivo.parcelas?.map(p => p.id) || [],
    })
    setMostrarForm(true)
  }

  const cerrarForm = () => {
    setMostrarForm(false)
    setEditando(null)
    setNuevaFase('')
    form.resetFields()
  }

  const guardar = async (values) => {
    setGuardando(true)
    try {
      const datos = {
        nombre: values.nombre,
        tipo_cultivo: values.tipo_cultivo,
        fecha_inicio: values.fecha_inicio?.format('YYYY-MM-DD') || null,
        fecha_fin: values.fecha_fin?.format('YYYY-MM-DD') || null,
        notas: values.notas || '',
        parcelas: values.parcelas || [],
        fases: fasesForm,
      }
      if (editando) {
        datos.estado = editando.estado
        await api(`/cultivos/${editando.id}`, { method: 'PUT', body: JSON.stringify(datos) })
        message.success('Cultivo actualizado')
      } else {
        await api('/cultivos', { method: 'POST', body: JSON.stringify(datos) })
        message.success('Cultivo creado')
      }
      cerrarForm()
      cargar()
    } catch (err) {
      message.error('Error guardando cultivo')
    }
    setGuardando(false)
  }

  const eliminar = async (id) => {
    try {
      await api(`/cultivos/${id}`, { method: 'DELETE' })
      message.success('Cultivo eliminado')
      if (detalle?.id === id) setDetalle(null)
      cargar()
    } catch (err) {
      message.error('Error eliminando cultivo')
    }
  }

  const toggleFase = async (cultivoId, index, completada) => {
    try {
      const result = await api(`/cultivos/${cultivoId}/fase/${index}`, {
        method: 'PUT', body: JSON.stringify({ completada }),
      })
      setCultivos(prev => prev.map(c => c.id === cultivoId ? { ...c, fases: result.fases, estado: result.estado } : c))
      if (detalle?.id === cultivoId) setDetalle(d => ({ ...d, fases: result.fases, estado: result.estado }))
    } catch (err) {
      message.error('Error actualizando fase')
    }
  }

  // === Acciones ===
  const abrirNuevaAccion = (tipo) => {
    setAccionTipo(tipo)
    setTipoCoste('directo')
    setProdsSeleccionados([])
    formAccion.resetFields()
    formAccion.setFieldsValue({ tipo, fecha: dayjs(), tipo_coste: 'directo' })
    setMostrarAccionForm(true)
  }

  const guardarAccion = async (values) => {
    try {
      const prodsEnviar = prodsSeleccionados.map(p => ({
        producto_id: p.id,
        nombre: p.nombre,
        cantidad: p.cantidad || 0,
        unidad: p.unidad_medida || 'L',
        precio_unitario: parseFloat(p.precio_unitario) || 0,
      }))
      await api(`/cultivos/${detalle.id}/acciones`, {
        method: 'POST',
        body: JSON.stringify({
          tipo: values.tipo,
          fecha: values.fecha.format('YYYY-MM-DD'),
          descripcion: values.descripcion || '',
          notas: values.notas || '',
          tipo_coste: tipoCoste,
          coste: tipoCoste === 'directo' ? (values.coste || 0) : 0,
          horas: tipoCoste === 'horas' ? (values.horas || 0) : 0,
          precio_hora: tipoCoste === 'horas' ? (values.precio_hora || 0) : 0,
          productos: prodsEnviar,
        }),
      })
      message.success('Acción registrada')
      setMostrarAccionForm(false)
      formAccion.resetFields()
      setProdsSeleccionados([])
      cargarDetalle(detalle.id)
      cargar() // refrescar productos/inventario
    } catch (err) {
      message.error('Error registrando acción')
    }
  }

  const toggleProducto = (prod, checked) => {
    if (checked) {
      setProdsSeleccionados([...prodsSeleccionados, { ...prod, cantidad: 1 }])
    } else {
      setProdsSeleccionados(prodsSeleccionados.filter(p => p.id !== prod.id))
    }
  }

  const eliminarAccion = async (accionId) => {
    try {
      await api(`/cultivos/${detalle.id}/acciones/${accionId}`, { method: 'DELETE' })
      cargarDetalle(detalle.id)
    } catch (err) {
      message.error('Error eliminando acción')
    }
  }

  // Fases form helpers
  const agregarFase = () => {
    if (!nuevaFase.trim()) return
    setFasesForm([...fasesForm, { nombre: nuevaFase.trim(), completada: false, fecha_completada: null }])
    setNuevaFase('')
  }
  const eliminarFase = (index) => setFasesForm(fasesForm.filter((_, i) => i !== index))
  const moverFase = (index, dir) => {
    const nuevo = [...fasesForm]
    const target = index + dir
    if (target < 0 || target >= nuevo.length) return
    ;[nuevo[index], nuevo[target]] = [nuevo[target], nuevo[index]]
    setFasesForm(nuevo)
  }

  const getProgreso = (fases) => {
    if (!fases || fases.length === 0) return 0
    return Math.round((fases.filter(f => f.completada).length / fases.length) * 100)
  }
  const getFaseActual = (fases) => fases?.find(f => !f.completada) || null

  // Agrupar acciones por tipo
  const accionesPorTipo = (tipo) => acciones.filter(a => a.tipo === tipo)
  const costePorTipo = (tipo) => {
    const item = costes.por_tipo?.find(c => c.tipo === tipo)
    return item ? parseFloat(item.total) : 0
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <div style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)', padding: isMobile ? '14px 14px' : '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <Title level={4} style={{ color: 'white', margin: 0, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {detalle ? detalle.nombre : 'Cultivos'}
        </Title>
        <Space>
          {detalle && <Button ghost size="small" onClick={() => { setDetalle(null); setAcciones([]); setCostes({ por_tipo: [], total: 0 }) }} style={{ flexShrink: 0 }}>Volver</Button>}
          {!detalle && !soloLectura && <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo} style={{ background: '#2E7D32', flexShrink: 0 }}>{!isMobile && 'Nuevo'}</Button>}
        </Space>
      </div>

      {/* Drawer crear/editar cultivo */}
      <Drawer
        title={editando ? 'Editar cultivo' : 'Nuevo cultivo'}
        open={mostrarForm}
        onClose={cerrarForm}
        width={isMobile ? '100%' : 420}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={cerrarForm}>Cancelar</Button>
            <Button type="primary" loading={guardando} onClick={() => form.submit()}>
              {editando ? 'Actualizar' : 'Crear cultivo'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={guardar}>
          <Form.Item label="Nombre del cultivo" name="nombre" rules={[{ required: true, message: 'Obligatorio' }]}>
            <Input placeholder="Ej: Patata cosecha 2026" />
          </Form.Item>
          <Form.Item label="Tipo de cultivo" name="tipo_cultivo" rules={[{ required: true, message: 'Obligatorio' }]}>
            <Input placeholder="Ej: Patata, Alcachofa, Limonero..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Fecha siembra" name="fecha_inicio">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Fecha est. recolección" name="fecha_fin">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Parcelas" name="parcelas" rules={[{ required: true, message: 'Selecciona al menos una' }]}>
            <Select mode="multiple" placeholder="Selecciona parcelas">
              {parcelas.map(p => <Select.Option key={p.id} value={p.id}>{p.nombre}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item label="Notas" name="notas">
            <TextArea rows={2} placeholder="Observaciones..." />
          </Form.Item>
          <Card size="small" title={<Text strong style={{ fontSize: '13px' }}>Fases / Tipos de acción</Text>} style={{ marginTop: '8px' }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {fasesForm.map((fase, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <Text style={{ flex: 1, fontSize: '13px' }}>{i + 1}. {fase.nombre}</Text>
                  <Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => moverFase(i, -1)} />
                  <Button type="text" size="small" icon={<ArrowDownOutlined />} disabled={i === fasesForm.length - 1} onClick={() => moverFase(i, 1)} />
                  <Button type="text" size="small" danger icon={<MinusCircleOutlined />} onClick={() => eliminarFase(i)} />
                </div>
              ))}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <Input size="small" placeholder="Nueva fase..." value={nuevaFase} onChange={e => setNuevaFase(e.target.value)} onPressEnter={agregarFase} style={{ flex: 1 }} />
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={agregarFase}>Añadir</Button>
              </div>
            </Space>
          </Card>
        </Form>
      </Drawer>

      {/* Drawer nueva acción */}
      <Drawer
        title={`Nueva acción: ${accionTipo}`}
        open={mostrarAccionForm}
        onClose={() => { setMostrarAccionForm(false); setProdsSeleccionados([]) }}
        width={isMobile ? '100%' : 400}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => { setMostrarAccionForm(false); setProdsSeleccionados([]) }}>Cancelar</Button>
            <Button type="primary" onClick={() => formAccion.submit()}>Registrar</Button>
          </Space>
        }
      >
        <Form form={formAccion} layout="vertical" onFinish={guardarAccion}>
          <Form.Item name="tipo" hidden><Input /></Form.Item>
          <Form.Item label="Fecha" name="fecha" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item label="Descripción" name="descripcion">
            <TextArea rows={2} placeholder="Qué se hizo..." />
          </Form.Item>

          {/* Tipo de coste */}
          <Form.Item label="Tipo de coste" name="tipo_coste">
            <Radio.Group value={tipoCoste} onChange={e => setTipoCoste(e.target.value)}>
              <Radio.Button value="directo"><EuroOutlined /> Importe</Radio.Button>
              <Radio.Button value="horas"><ClockCircleOutlined /> Horas</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {tipoCoste === 'directo' ? (
            <Form.Item label="Importe (€)" name="coste">
              <InputNumber min={0} step={0.5} style={{ width: '100%' }} placeholder="0.00" />
            </Form.Item>
          ) : (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item label="Horas" name="horas">
                  <InputNumber min={0} step={0.5} style={{ width: '100%' }} placeholder="Ej: 3" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="€/hora" name="precio_hora">
                  <InputNumber min={0} step={0.5} style={{ width: '100%' }} placeholder="Ej: 45" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Productos */}
          {productos.length > 0 && (() => {
            const categorias = [...new Set(productos.map(p => p.categoria).filter(Boolean))]
            return (
              <>
                <Divider style={{ margin: '12px 0', fontSize: '13px' }}>Productos utilizados</Divider>
                {categorias.map(cat => {
                  const prodsCategoria = productos.filter(p => p.categoria === cat)
                  return (
                    <div key={cat} style={{ marginBottom: '10px' }}>
                      <Text strong style={{ fontSize: '12px', color: '#1B3A6B', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>{cat}</Text>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {prodsCategoria.map(prod => {
                          const sel = prodsSeleccionados.find(p => p.id === prod.id)
                          return (
                            <Card key={prod.id} size="small" style={{ borderRadius: '8px' }}>
                              <Checkbox checked={!!sel} onChange={e => toggleProducto(prod, e.target.checked)}>
                                <Text style={{ fontSize: '13px' }}>{prod.nombre}</Text>
                                <Text type="secondary" style={{ fontSize: '11px', marginLeft: '6px' }}>
                                  (Stock: {prod.stock_actual} {prod.unidad_medida})
                                  {prod.precio_unitario > 0 && <> · {prod.precio_unitario} €/{prod.unidad_medida}</>}
                                </Text>
                              </Checkbox>
                              {sel && (
                                <div style={{ marginTop: '6px', marginLeft: '24px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <InputNumber
                                    size="small" min={0.1} step={0.5}
                                    value={sel.cantidad}
                                    onChange={v => setProdsSeleccionados(prodsSeleccionados.map(p => p.id === prod.id ? { ...p, cantidad: v } : p))}
                                    style={{ width: '80px' }}
                                  />
                                  <Text type="secondary" style={{ fontSize: '12px' }}>{prod.unidad_medida || 'L'}</Text>
                                  {prod.precio_unitario > 0 && (
                                    <Text style={{ fontSize: '12px', color: '#E65100' }}>
                                      = {((sel.cantidad || 0) * prod.precio_unitario).toFixed(2)} €
                                    </Text>
                                  )}
                                </div>
                              )}
                            </Card>
                          )
                        })}
                      </Space>
                    </div>
                  )
                })}
              </>
            )
          })()}

          <Form.Item label="Notas" name="notas" style={{ marginTop: '12px' }}>
            <TextArea rows={2} placeholder="Observaciones..." />
          </Form.Item>
        </Form>
      </Drawer>

      <div style={{ padding: '16px' }}>
        {cargando ? (
          <div style={{ textAlign: 'center', marginTop: '60px' }}><Spin size="large" /></div>
        ) : detalle ? (
          /* === VISTA DETALLE === */
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {/* Resumen */}
            <Card size="small" style={{ borderRadius: '12px' }}>
              <Row gutter={12}>
                <Col span={16}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>{detalle.tipo_cultivo}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Parcelas: {detalle.parcelas?.map(p => p.nombre).join(', ')}
                  </Text>
                  {detalle.fecha_inicio && (
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                      <CalendarOutlined /> {dayjs(detalle.fecha_inicio).format('DD/MM/YYYY')} → {detalle.fecha_fin ? dayjs(detalle.fecha_fin).format('DD/MM/YYYY') : '...'}
                    </Text>
                  )}
                  <Progress percent={getProgreso(detalle.fases)} strokeColor="#2E7D32" size="small" style={{ marginTop: '8px' }} />
                </Col>
                <Col span={8} style={{ textAlign: 'right' }}>
                  <Text type="secondary" style={{ fontSize: '11px' }}>Coste total</Text>
                  <Title level={4} style={{ margin: 0, color: '#E65100' }}>
                    {costes.total.toFixed(2)} €
                  </Title>
                </Col>
              </Row>
            </Card>

            {/* Fases con acciones */}
            {(detalle.fases || []).map((fase, i) => {
              const accionesFase = accionesPorTipo(fase.nombre)
              const costeFase = costePorTipo(fase.nombre)
              return (
                <Card
                  key={i}
                  size="small"
                  style={{
                    borderRadius: '12px',
                    borderLeft: fase.completada ? '3px solid #52c41a' : accionesFase.length > 0 ? '3px solid #1890ff' : '3px solid #d9d9d9'
                  }}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Checkbox checked={fase.completada} onChange={(e) => toggleFase(detalle.id, i, e.target.checked)} />
                      <Text strong style={{ flex: 1, fontSize: '14px', textDecoration: fase.completada ? 'line-through' : 'none', color: fase.completada ? '#999' : '#333' }}>
                        {fase.nombre}
                      </Text>
                      {costeFase > 0 && <Tag color="orange"><EuroOutlined /> {costeFase.toFixed(2)} €</Tag>}
                      <Badge count={accionesFase.length} style={{ backgroundColor: '#1890ff' }} />
                    </div>
                  }
                  extra={!soloLectura && (
                    <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => abrirNuevaAccion(fase.nombre)} style={{ background: '#2E7D32' }}>
                      Acción
                    </Button>
                  )}
                >
                  {accionesFase.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: '12px' }}>Sin acciones registradas</Text>
                  ) : (
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      {accionesFase.map(a => {
                        const prods = a.productos || []
                        return (
                          <div key={a.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px', background: '#fafafa', borderRadius: '6px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <Text style={{ fontSize: '12px' }}><CalendarOutlined /> {dayjs(a.fecha).format('DD/MM/YYYY')}</Text>
                                {a.coste > 0 && <Tag color="orange" style={{ fontSize: '11px' }}>{parseFloat(a.coste).toFixed(2)} €</Tag>}
                                {a.tipo_coste === 'horas' && a.horas > 0 && (
                                  <Tag color="blue" style={{ fontSize: '11px' }}><ClockCircleOutlined /> {a.horas}h x {a.precio_hora}€</Tag>
                                )}
                              </div>
                              {a.descripcion && <Text style={{ fontSize: '12px', display: 'block', marginTop: '2px' }}>{a.descripcion}</Text>}
                              {prods.length > 0 && (
                                <div style={{ marginTop: '4px' }}>
                                  {prods.map((p, pi) => (
                                    <Tag key={pi} color="green" style={{ fontSize: '10px', marginBottom: '2px' }}>
                                      {p.nombre}: {p.cantidad} {p.unidad} ({(p.cantidad * p.precio_unitario).toFixed(2)}€)
                                    </Tag>
                                  ))}
                                </div>
                              )}
                              {a.notas && <Text type="secondary" style={{ fontSize: '11px', display: 'block', fontStyle: 'italic', marginTop: '2px' }}>{a.notas}</Text>}
                            </div>
                            {!soloLectura && (
                              <Popconfirm title="¿Eliminar?" onConfirm={() => eliminarAccion(a.id)} okText="Sí" cancelText="No">
                                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                              </Popconfirm>
                            )}
                          </div>
                        )
                      })}
                    </Space>
                  )}
                </Card>
              )
            })}
          </Space>
        ) : cultivos.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size={4}>
                <Text type="secondary">No hay cultivos todavía</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>Pulsa "Nuevo" para crear el primer cultivo</Text>
              </Space>
            }
            style={{ marginTop: '60px' }}
          />
        ) : (
          /* === LISTADO === */
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {cultivos.map(c => {
              const progreso = getProgreso(c.fases)
              const faseActual = getFaseActual(c.fases)
              return (
                <Card key={c.id} size="small" hoverable
                  style={{ borderRadius: '12px', borderLeft: c.estado === 'finalizado' ? '3px solid #52c41a' : '3px solid #1890ff' }}
                  onClick={() => abrirDetalle(c)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ fontSize: '15px', color: '#1B3A6B' }}>{c.nombre}</Text>
                      {c.parcelas && c.parcelas.length > 0 && (
                        <Text style={{ fontSize: '13px', color: '#2E7D32', marginLeft: '8px', fontWeight: 600 }}>
                          {calcTahullas(c.parcelas).toFixed(1)} tahúllas
                        </Text>
                      )}
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>{c.tipo_cultivo}</Text>
                      {c.parcelas && c.parcelas.length > 0 && (
                        <div style={{ marginTop: '4px' }}>
                          {c.parcelas.map(p => <Tag key={p.id} style={{ fontSize: '11px', marginBottom: '2px' }}>{p.nombre}</Tag>)}
                        </div>
                      )}
                    </div>
                    <Space size={4} onClick={e => e.stopPropagation()}>
                      {c.estado === 'finalizado' && <Tag color="success">Finalizado</Tag>}
                      {!soloLectura && (
                        <>
                          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => abrirEdicion(c)} />
                          <Popconfirm title="¿Eliminar este cultivo?" onConfirm={() => eliminar(c.id)} okText="Sí" cancelText="No">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </>
                      )}
                    </Space>
                  </div>
                  <Progress percent={progreso} strokeColor="#2E7D32" size="small" style={{ marginTop: '8px' }} />
                  {faseActual && <Text type="secondary" style={{ fontSize: '11px' }}>Fase actual: {faseActual.nombre}</Text>}
                  {c.fecha_inicio && (
                    <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
                      {dayjs(c.fecha_inicio).format('DD/MM/YYYY')} → {c.fecha_fin ? dayjs(c.fecha_fin).format('DD/MM/YYYY') : '...'}
                    </Text>
                  )}
                </Card>
              )
            })}
          </Space>
        )}
      </div>
    </div>
  )
}
